import { Injectable } from '@angular/core';

export type HeroMetaItem = {
  title: string;
  description: string;
};

export type HeroAction = {
  label: string;
  href: string;
  variant: 'primary' | 'ghost';
};

export type HeroContent = {
  badge: string;
  title: string;
  accent: string;
  lede: string;
  actions: HeroAction[];
  meta: HeroMetaItem[];
};

export type HeroSlide = {
  title: string;
  body: string;
  tags: string[];
  chips: string[];
};

@Injectable({ providedIn: 'root' })
export class HeroSectionService {
  readonly content: HeroContent = {
    badge: 'Designed for pet parents & care pros',
    title: 'Ruffles & Shine',
    accent: 'pet care, beautifully coordinated.',
    lede:
      'One premium platform that blends grooming, clinic, retail, and day care with effortless booking, live updates, and seamless handoffs.',
    actions: [
      {
        label: 'Book a Service',
        href: 'https://grooming.rufflesandshinegrooming.co.uk',
        variant: 'primary'
      },
      { label: 'Explore Services', href: '#services', variant: 'ghost' }
    ],
    meta: [
      {
        title: 'Unified booking',
        description: 'Schedule grooming, clinic, retail, and day care in one flow.'
      },
      {
        title: 'Care-team handoffs',
        description: 'Shared notes keep every visit consistent and calm.'
      },
      {
        title: 'Live updates',
        description: 'Status, reminders, and receipts in one timeline.'
      }
    ]
  };

  readonly slides: HeroSlide[] = [
    {
      title: 'Grooming Studio',
      body: 'Signature bathing, breed-true styling, and coat care designed around each pet.',
      tags: ['Breed artistry', 'Coat analysis', 'Stylist match'],
      chips: ['Bath ritual', 'Brush & blowout', 'Deshedding', 'Breed styling']
    },
    {
      title: 'Smart Booking',
      body: 'Luxury scheduling that remembers preferences, bundles add-ons, and keeps every visit effortless.',
      tags: ['One-tap rebook', 'Add-on bundles', 'Auto reminders'],
      chips: ['Instant rebook', 'Deposit holds', 'Add-on menu', 'VIP notes']
    },
    {
      title: 'Live Updates',
      body: 'A premium care timeline with beautiful status moments and timely ready alerts.',
      tags: ['Live timeline', 'Photo moments', 'Ready alerts'],
      chips: ['Check-in', 'Wash', 'Dry & style', 'Ready pickup']
    },
    {
      title: 'Premium Add-ons',
      body: 'Spa-grade upgrades that elevate every visit with gentle, add-on treatments.',
      tags: ['Spa upgrades', 'Gentle care', 'Wellness finish'],
      chips: ['Nail trim', 'Ear care', 'Spa masque', 'Dental freshen']
    }
  ];
}
